import React, { useState, useCallback } from 'react';
import { QuizView } from './QuizView';
import type { QuizQuestion as QuizQuestionType } from '../../types/course';

interface Props {
  questions: QuizQuestionType[];
  onComplete: (score: number, total: number) => void;
  onBack: () => void;
}

export const FinalQuizView: React.FC<Props> = ({ questions, onComplete, onBack }) => {
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);

  const handleQuizComplete = useCallback(
    (s: number, t: number) => {
      setScore(s);
      setTotal(t);
      setFinished(true);
      onComplete(s, t);
    },
    [onComplete]
  );

  const pct = total > 0 ? Math.round((score / total) * 100) : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--clue-border-mid)]">
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--clue-bg-secondary)] transition-colors"
          aria-label="Back to course"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--clue-text-secondary)]">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div>
          <h2 className="text-base font-semibold font-serif text-[var(--clue-text-primary)]">
            Final Quiz
          </h2>
          <p className="text-xs text-[var(--clue-text-tertiary)]">
            Test everything you&apos;ve learned
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {!finished ? (
          <QuizView questions={questions} onComplete={handleQuizComplete} />
        ) : (
          <div className="flex flex-col items-center justify-center gap-6 py-8 text-center">
            <div className="text-5xl">
              {pct >= 80 ? '🎓' : pct >= 60 ? '👏' : '📚'}
            </div>
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold font-mono border-4 ${
                pct >= 60
                  ? 'border-[var(--clue-green-accent)] text-[var(--clue-green-accent)]'
                  : 'border-amber-400 text-amber-400'
              }`}
            >
              {pct}%
            </div>
            <div>
              <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)]">
                {pct >= 80
                  ? 'Outstanding!'
                  : pct >= 60
                    ? 'Well done!'
                    : 'Keep practicing!'}
              </h3>
              <p className="text-sm text-[var(--clue-text-secondary)] mt-1">
                You scored {score}/{total} on the Final Quiz
              </p>
            </div>
            <p className="text-sm text-[var(--clue-text-secondary)] max-w-xs leading-relaxed">
              You&apos;ve completed Foundations to Fundamental Analysis. Use what you&apos;ve learned to dominate your next Wallstreetle puzzle!
            </p>
            <button
              onClick={onBack}
              className="h-11 px-8 rounded-lg bg-[var(--clue-green-accent)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Back to Course
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
